import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  ArrowsPointingInIcon,
  CheckIcon,
  FolderArrowDownIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Select from 'react-select';
import FolderPicker from './FolderPicker';
import { referenceAPI, usersAPI } from '../../api/api';
import { translateDepartment, translateDepartments } from './departmentLabels';
import { docConvertAPI, dailyNoteImageUrl, downloadDailyNoteDocument } from '../../api/docConvertApi';
import ImagePreview from '../../components/image/ImagePreview';
import ActionToolbar from '../../components/ui/ActionToolbar';
import DataTable from '../../components/ui/DataTable';
import FilterActions from '../../components/ui/FilterActions';
import FilterPanel from '../../components/ui/FilterPanel';
import IconActionButton from '../../components/ui/IconActionButton';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import ResponsiveModal from '../../components/ui/ResponsiveModal';
import { useTheme } from '../../contexts/ThemeContext';

const PAGE_SIZE = 50;

const EMPTY_FORM = {
  ilgili_not_adi: '',
  not_tarihi: '',
  not_basligi: '',
  not: '',
  donusler: '',
  ilgili_departman: '',
  ilgili_kisi: '',
  durum: '',
};

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 28,
    fontSize: 11,
    backgroundColor: 'transparent',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999, fontSize: 11 }),
};

const filterSelectClassNames = {
  control: () =>
    '!min-h-[28px] !text-[11px] !border-0 !shadow-none !bg-transparent !text-gray-900 dark:!text-gray-100',
  singleValue: () => '!text-[11px] !text-gray-900 dark:!text-gray-100',
  input: () => '!text-[11px] !text-gray-900 dark:!text-gray-100',
  placeholder: () => '!text-[11px] !text-gray-400 dark:!text-gray-500',
  menu: () =>
    '!text-[11px] !bg-white dark:!bg-gray-800 !border !border-gray-200 dark:!border-gray-700 !rounded-md !shadow-lg',
  menuList: () => 'py-1',
  option: ({ isFocused, isSelected }) =>
    [
      '!cursor-pointer !text-[11px] !text-gray-900 dark:!text-gray-100',
      isFocused ? '!bg-gray-100 dark:!bg-gray-700' : '',
      isSelected ? '!bg-blue-100 dark:!bg-blue-900/50' : '',
    ]
      .filter(Boolean)
      .join(' '),
  dropdownIndicator: () => '!text-gray-500 dark:!text-gray-400',
  clearIndicator: () => '!text-gray-500 dark:!text-gray-400 hover:!text-gray-700 dark:hover:!text-gray-200',
  noOptionsMessage: () => '!text-[11px] !text-gray-500 dark:!text-gray-400',
};

const modalSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 36,
    fontSize: 13,
    backgroundColor: 'transparent',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999, fontSize: 13 }),
};

const modalSelectClassNames = {
  control: ({ isFocused }) =>
    [
      '!min-h-[36px] !rounded-md !border !shadow-none',
      '!border-gray-300 dark:!border-gray-600',
      '!bg-white dark:!bg-gray-700',
      isFocused ? '!border-blue-500 dark:!border-blue-400 !ring-1 !ring-blue-500/30' : '',
    ]
      .filter(Boolean)
      .join(' '),
  menu: () =>
    '!text-sm !bg-white dark:!bg-gray-800 !border !border-gray-200 dark:!border-gray-700 !rounded-md !shadow-lg',
  menuList: () => 'py-1',
  option: ({ isFocused, isSelected }) =>
    [
      '!cursor-pointer !text-sm !text-gray-900 dark:!text-gray-100',
      isFocused ? '!bg-gray-100 dark:!bg-gray-700' : '',
      isSelected ? '!bg-blue-100 dark:!bg-blue-900/50 !text-gray-900 dark:!text-white' : '',
    ]
      .filter(Boolean)
      .join(' '),
  singleValue: () => '!text-gray-900 dark:!text-white',
  input: () => '!text-gray-900 dark:!text-white',
  placeholder: () => '!text-gray-400 dark:!text-gray-500',
  multiValue: () => '!bg-blue-50 dark:!bg-blue-900/40 !rounded',
  multiValueLabel: () => '!text-xs !text-blue-800 dark:!text-blue-100',
  multiValueRemove: () =>
    '!text-blue-600 dark:!text-blue-200 hover:!bg-blue-200 dark:hover:!bg-blue-800 hover:!text-red-600 dark:hover:!text-red-300',
  dropdownIndicator: () => '!text-gray-500 dark:!text-gray-400',
  clearIndicator: () => '!text-gray-500 dark:!text-gray-400 hover:!text-gray-700 dark:hover:!text-gray-200',
  noOptionsMessage: () => '!text-sm !text-gray-500 dark:!text-gray-400',
};

const buildThemedSelectStyles = (baseStyles, isDarkMode, { compact = false } = {}) => {
  const fontSize = compact ? 11 : 13;
  const textColor = isDarkMode ? '#f3f4f6' : '#111827';
  const mutedColor = isDarkMode ? '#9ca3af' : '#6b7280';
  const menuBg = isDarkMode ? '#1f2937' : '#ffffff';
  const menuBorder = isDarkMode ? '#374151' : '#e5e7eb';
  const focusBg = isDarkMode ? '#374151' : '#f3f4f6';
  const selectedBg = isDarkMode ? 'rgba(30, 58, 138, 0.5)' : '#dbeafe';

  return {
    ...baseStyles,
    control: (base, state) => {
      const themedBase = baseStyles.control ? baseStyles.control(base, state) : base;
      if (compact) {
        return {
          ...themedBase,
          color: textColor,
        };
      }
      return {
        ...themedBase,
        backgroundColor: isDarkMode ? '#374151' : '#ffffff',
        borderColor: state.isFocused
          ? (isDarkMode ? '#60a5fa' : '#3b82f6')
          : (isDarkMode ? '#4b5563' : '#d1d5db'),
        boxShadow: state.isFocused
          ? `0 0 0 1px ${isDarkMode ? 'rgba(96, 165, 250, 0.3)' : 'rgba(59, 130, 246, 0.3)'}`
          : 'none',
        '&:hover': {
          borderColor: isDarkMode ? '#6b7280' : '#9ca3af',
        },
      };
    },
    menu: (base) => ({
      ...(baseStyles.menu ? baseStyles.menu(base) : base),
      backgroundColor: menuBg,
      border: `1px solid ${menuBorder}`,
      boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
      fontSize,
    }),
    menuList: (base) => ({
      ...base,
      backgroundColor: menuBg,
    }),
    option: (base, state) => ({
      ...base,
      fontSize,
      backgroundColor: state.isSelected
        ? selectedBg
        : state.isFocused
          ? focusBg
          : menuBg,
      color: textColor,
      cursor: 'pointer',
    }),
    singleValue: (base) => ({
      ...base,
      color: textColor,
    }),
    input: (base) => ({
      ...base,
      color: textColor,
    }),
    placeholder: (base) => ({
      ...base,
      color: mutedColor,
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: mutedColor,
    }),
    clearIndicator: (base) => ({
      ...base,
      color: mutedColor,
      ':hover': {
        color: isDarkMode ? '#e5e7eb' : '#374151',
      },
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: isDarkMode ? 'rgba(30, 58, 138, 0.4)' : '#eff6ff',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: isDarkMode ? '#bfdbfe' : '#1e40af',
      fontSize: compact ? 11 : 12,
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: isDarkMode ? '#93c5fd' : '#2563eb',
      ':hover': {
        backgroundColor: isDarkMode ? '#1e3a8a' : '#dbeafe',
        color: isDarkMode ? '#fca5a5' : '#dc2626',
      },
    }),
    noOptionsMessage: (base) => ({
      ...base,
      color: mutedColor,
      fontSize,
    }),
  };
};

const NOTE_TEXTAREA_CLASS =
  'w-full min-h-[11rem] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white resize-y';

const DOCUMENT_ACCEPT =
  '.doc,.docx,.xls,.xlsx,.pdf,.ppt,.pptx,.txt,.csv,.rtf,.odt,.ods';

const STATUS_OPTIONS = [
  { value: 'Açık', label: 'Açık' },
  { value: 'Kapalı', label: 'Kapalı' },
  { value: 'Dönüş yapıldı', label: 'Dönüş yapıldı' },
];

const getStatusBadgeClass = (value) => {
  switch (value) {
    case 'Açık':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    case 'Kapalı':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    case 'Dönüş yapıldı':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  }
};

const buildStatusOptions = (currentValue = '') => {
  const trimmed = String(currentValue || '').trim();
  if (trimmed && !STATUS_OPTIONS.some((option) => option.value === trimmed)) {
    return [...STATUS_OPTIONS, { value: trimmed, label: trimmed }];
  }
  return STATUS_OPTIONS;
};

const toSelectOptions = (values = []) =>
  values
    .map((value) => String(value ?? '').trim())
    .filter(Boolean)
    .map((value) => ({ value, label: value }));

const compareLabels = (a, b) =>
  String(a?.label ?? a ?? '').localeCompare(String(b?.label ?? b ?? ''), 'tr');

const mergeStringOptions = (...lists) => {
  const merged = new Set();
  lists.flat().forEach((item) => {
    const text = String(item ?? '').trim();
    if (text) merged.add(text);
  });
  return toSelectOptions([...merged].sort((a, b) => a.localeCompare(b, 'tr')));
};

const MULTI_VALUE_SEPARATOR = ', ';

const parseMultiValue = (value) => {
  if (!value) return [];
  return String(value)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
};

const joinMultiValue = (values = []) =>
  values
    .map((value) => String(value ?? '').trim())
    .filter(Boolean)
    .join(MULTI_VALUE_SEPARATOR);

const findMultiSelectOptions = (options, value) => {
  const selectedValues = parseMultiValue(value);
  const optionMap = new Map(options.map((option) => [option.value, option]));
  return selectedValues.map(
    (selectedValue) => optionMap.get(selectedValue) || { value: selectedValue, label: selectedValue }
  );
};

const extractList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.results)) return response.results;
  return [];
};

const userLabel = (user) => {
  if (!user) return '';
  return (
    user.full_name?.trim()
    || [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
    || user.email
    || ''
  );
};

/** GG.AA.YYYY veya YYYY-MM-DD → date input (YYYY-MM-DD) */
const toDateInputValue = (value) => {
  if (!value) return '';
  const trimmed = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const trMatch = trimmed.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (trMatch) {
    const [, day, month, year] = trMatch;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  return '';
};

const formatNoteDateDisplay = (value) => {
  const iso = toDateInputValue(value);
  if (!iso) return value || '—';
  const [year, month, day] = iso.split('-');
  return `${day}.${month}.${year}`;
};

const formatDateTimeDisplay = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
};

const toDateFilterOptions = (values = []) =>
  values.map((value) => ({
    value,
    label: formatNoteDateDisplay(value),
  }));

const toPreviewImages = (images = []) =>
  images.map((image) => ({
    id: image.id,
    name: image.original_name || 'Görsel',
    preview: dailyNoteImageUrl(image.id),
    thumbnail_url: dailyNoteImageUrl(image.id),
  }));

const getNoteImages = (row) => row?.note_images ?? row?.images ?? [];
const getReturnImages = (row) => row?.return_images ?? [];
const getNoteDocuments = (row) => row?.note_documents ?? [];
const getReturnDocuments = (row) => row?.return_documents ?? [];

const StatusPicker = ({ value, onChange, options = STATUS_OPTIONS }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((option) => {
      const selected = value === option.value;
      return (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(selected ? '' : option.value)}
          className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
            selected
              ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-900/30 dark:text-blue-100'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          <span
            className={`flex h-3.5 w-3.5 items-center justify-center rounded border ${
              selected
                ? 'border-blue-500 bg-blue-500 dark:border-blue-400 dark:bg-blue-500'
                : 'border-gray-400 dark:border-gray-500'
            }`}
          >
            {selected && <CheckIcon className="h-2.5 w-2.5 text-white" />}
          </span>
          {option.label}
        </button>
      );
    })}
  </div>
);

const ModalImageField = ({
  label,
  existingImages = [],
  removeImageIds = [],
  onToggleRemove,
  onOpenPreview,
  onFilesSelected,
  pendingFiles = [],
  onRemovePending,
  hint,
}) => (
  <div className="space-y-1 md:col-span-2">
    <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">{label}</Label>
    {pendingFiles.length > 0 && (
      <div className="flex flex-wrap gap-2 mb-2">
        {pendingFiles.map((file, index) => (
          <span
            key={`${file.name}-${index}`}
            className="inline-flex items-center gap-1 rounded border border-blue-200 bg-blue-50 px-2 py-1 text-xs text-blue-800 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-200"
          >
            <span className="max-w-[160px] truncate" title={file.name}>{file.name}</span>
            <button
              type="button"
              onClick={() => onRemovePending?.(index)}
              className="rounded-full p-0.5 text-blue-500 hover:bg-red-600 hover:text-white"
              title="Kaldır"
            >
              <XMarkIcon className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
    )}
    {existingImages.length > 0 && (
      <div className="flex flex-wrap gap-2 mb-2">
        {existingImages.map((image, index) => (
          <div
            key={image.id}
            className={`relative rounded border p-1 ${
              removeImageIds.includes(image.id)
                ? 'border-red-400 opacity-50'
                : 'border-gray-200 dark:border-gray-600'
            }`}
          >
            <button
              type="button"
              onClick={() => onOpenPreview(existingImages, index)}
              className="block h-16 w-16 overflow-hidden rounded hover:ring-2 hover:ring-highlight-500"
              title="Büyüt"
            >
              <img
                src={dailyNoteImageUrl(image.id)}
                alt={image.original_name}
                className="h-full w-full object-cover"
              />
            </button>
            <button
              type="button"
              onClick={() => onToggleRemove(image.id)}
              className={`absolute -right-1 -top-1 rounded-full p-0.5 ${
                removeImageIds.includes(image.id)
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-800/80 text-white hover:bg-red-600'
              }`}
              title={removeImageIds.includes(image.id) ? 'Silmeyi geri al' : 'Sil'}
            >
              <XMarkIcon className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    )}
    <input
      type="file"
      accept="image/*"
      multiple
      onChange={(e) => {
        onFilesSelected(Array.from(e.target.files || []));
        e.target.value = '';
      }}
      className="block w-full text-sm text-gray-600 dark:text-gray-300"
    />
    {hint && (
      <p className="text-[11px] text-gray-500 dark:text-gray-400">{hint}</p>
    )}
  </div>
);

const ModalDocumentField = ({
  label,
  existingDocuments = [],
  pendingFiles = [],
  removeDocumentIds = [],
  onToggleRemove,
  onFilesSelected,
  onRemovePending,
  hint,
}) => (
  <div className="space-y-1 md:col-span-2">
    <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">{label}</Label>
    {existingDocuments.length > 0 && (
      <div className="flex flex-wrap gap-2 mb-2">
        {existingDocuments.map((document) => (
          <div
            key={document.id}
            className={`flex items-center gap-1 rounded border px-2 py-1 text-xs ${
              removeDocumentIds.includes(document.id)
                ? 'border-red-400 opacity-50'
                : 'border-gray-200 dark:border-gray-600'
            }`}
          >
            <span className="max-w-[180px] truncate" title={document.original_name}>
              {document.original_name || 'Döküman'}
            </span>
            <button
              type="button"
              onClick={() => onToggleRemove(document.id)}
              className={`rounded-full p-0.5 ${
                removeDocumentIds.includes(document.id)
                  ? 'bg-red-600 text-white'
                  : 'text-gray-500 hover:bg-red-600 hover:text-white'
              }`}
              title={removeDocumentIds.includes(document.id) ? 'Silmeyi geri al' : 'Sil'}
            >
              <XMarkIcon className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    )}
    {pendingFiles.length > 0 && (
      <div className="flex flex-wrap gap-2 mb-2">
        {pendingFiles.map((file, index) => (
          <span
            key={`${file.name}-${index}`}
            className="inline-flex items-center gap-1 rounded border border-blue-200 bg-blue-50 px-2 py-1 text-xs text-blue-800 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-200"
          >
            <span className="max-w-[180px] truncate" title={file.name}>{file.name}</span>
            <button
              type="button"
              onClick={() => onRemovePending?.(index)}
              className="rounded-full p-0.5 text-blue-500 hover:bg-red-600 hover:text-white"
              title="Kaldır"
            >
              <XMarkIcon className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
    )}
    <input
      type="file"
      accept={DOCUMENT_ACCEPT}
      multiple
      onChange={(e) => {
        onFilesSelected(Array.from(e.target.files || []));
        e.target.value = '';
      }}
      className="block w-full text-sm text-gray-600 dark:text-gray-300"
    />
    {hint && (
      <p className="text-[11px] text-gray-500 dark:text-gray-400">{hint}</p>
    )}
  </div>
);

const ImageThumbnailStrip = ({ images = [], onOpenPreview }) => {
  if (!images.length) {
    return <span className="text-gray-400">-</span>;
  }

  return (
    <div className="flex flex-wrap gap-1" onClick={(event) => event.stopPropagation()}>
      {images.map((image, index) => (
        <button
          key={image.id ?? index}
          type="button"
          title={image.original_name || 'Görseli büyüt'}
          onClick={() => onOpenPreview(images, index)}
          className="h-10 w-10 shrink-0 overflow-hidden rounded border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 hover:ring-2 hover:ring-highlight-500 transition-shadow"
        >
          <img
            src={dailyNoteImageUrl(image.id)}
            alt={image.original_name || 'Görsel'}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        </button>
      ))}
    </div>
  );
};

const DocumentDownloadStrip = ({ documents = [], onDownloadError }) => {
  if (!documents.length) {
    return <span className="text-gray-400">-</span>;
  }

  return (
    <div className="flex flex-col gap-1" onClick={(event) => event.stopPropagation()}>
      {documents.map((document) => (
        <button
          key={document.id}
          type="button"
          title={document.original_name || 'İndir'}
          onClick={async () => {
            try {
              await downloadDailyNoteDocument(document.id, document.original_name);
            } catch (err) {
              onDownloadError?.(err.message || 'Döküman indirilemedi.');
            }
          }}
          className="inline-flex max-w-[160px] items-center gap-1 rounded border border-gray-200 px-2 py-0.5 text-[11px] text-blue-600 hover:bg-gray-50 dark:border-gray-600 dark:text-blue-400 dark:hover:bg-gray-700"
        >
          <ArrowDownTrayIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{document.original_name || 'Döküman'}</span>
        </button>
      ))}
    </div>
  );
};

const DailyNotesPage = () => {
  const { isDarkMode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const openedNoteIdRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({ count: 0, totalPages: 1 });
  const [sorting, setSorting] = useState([]);
  const [filterOptions, setFilterOptions] = useState({});
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [newNoteImages, setNewNoteImages] = useState([]);
  const [newReturnImages, setNewReturnImages] = useState([]);
  const [existingNoteImages, setExistingNoteImages] = useState([]);
  const [existingReturnImages, setExistingReturnImages] = useState([]);
  const [removeImageIds, setRemoveImageIds] = useState([]);
  const [newNoteDocuments, setNewNoteDocuments] = useState([]);
  const [newReturnDocuments, setNewReturnDocuments] = useState([]);
  const [existingNoteDocuments, setExistingNoteDocuments] = useState([]);
  const [existingReturnDocuments, setExistingReturnDocuments] = useState([]);
  const [removeDocumentIds, setRemoveDocumentIds] = useState([]);
  const [modalTab, setModalTab] = useState('not');
  const [returnEntries, setReturnEntries] = useState([]);
  const [newReturnText, setNewReturnText] = useState('');
  const [returnDraftOpen, setReturnDraftOpen] = useState(false);
  const [returnBusy, setReturnBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [isMergeOpen, setIsMergeOpen] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState(null);
  const [mergeBusy, setMergeBusy] = useState(false);
  const [isSendOpen, setIsSendOpen] = useState(false);
  const [sendFolderId, setSendFolderId] = useState(null);
  const [sendFolderName, setSendFolderName] = useState('Klasörsüz (kök)');
  const [sendBusy, setSendBusy] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [filters, setFilters] = useState({
    ilgili_not_adi: '',
    ilgili_departman: '',
    ilgili_kisi: '',
    durum: '',
    not_tarihi: '',
    search: '',
  });
  const [draftFilters, setDraftFilters] = useState({
    ilgili_not_adi: '',
    ilgili_departman: '',
    ilgili_kisi: '',
    durum: '',
    not_tarihi: '',
    search: '',
  });

  const loadData = async (activeFilters = filters, pageArg = page, sortArg = sorting) => {
    setLoading(true);
    setError(null);
    try {
      const params = { ...activeFilters, page: pageArg, page_size: PAGE_SIZE };
      if (sortArg?.length) {
        params.ordering = `${sortArg[0].desc ? '-' : ''}${sortArg[0].id}`;
      }
      const [listResponse, optionsResponse] = await Promise.all([
        docConvertAPI.getDailyNotes(params),
        docConvertAPI.getDailyNoteFilterOptions(),
      ]);
      setRows(listResponse?.data || []);
      const resolvedPage = listResponse?.page ?? pageArg;
      setPage(resolvedPage);
      setPageInfo({
        count: listResponse?.count ?? (listResponse?.data?.length || 0),
        totalPages: listResponse?.total_pages ?? 1,
      });
      setSelectedIds(new Set());
      setFilterOptions(optionsResponse?.data || {});
    } catch (err) {
      setError(err.message || 'Notlar yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const goToPage = (targetPage) => {
    const clamped = Math.min(Math.max(1, targetPage), pageInfo.totalPages || 1);
    if (clamped === page) return;
    loadData(filters, clamped, sorting);
  };

  // Kolon başlığına tıklanınca sunucu taraflı sıralama (tüm veri üzerinde, 1. sayfaya döner).
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    setSorting(next);
    loadData(filters, 1, next);
  };

  useEffect(() => {
    loadData();
    Promise.all([referenceAPI.getDepartments(), usersAPI.getUsers()])
      .then(([departmentsResponse, usersResponse]) => {
        setDepartments(extractList(departmentsResponse));
        setUsers(extractList(usersResponse));
      })
      .catch(() => {
        setDepartments([]);
        setUsers([]);
      });
  }, []);

  const themedFilterSelectStyles = useMemo(
    () => buildThemedSelectStyles(selectStyles, isDarkMode, { compact: true }),
    [isDarkMode]
  );

  const themedModalSelectStyles = useMemo(
    () => buildThemedSelectStyles(modalSelectStyles, isDarkMode, { compact: false }),
    [isDarkMode]
  );

  const departmentOptions = useMemo(
    () =>
      mergeStringOptions(
        departments.map((department) => department.department_name),
        filterOptions.ilgili_departman || []
      ).map((option) => ({ value: option.value, label: translateDepartment(option.value) })),
    [departments, filterOptions.ilgili_departman]
  );

  const userOptions = useMemo(() => {
    const selectedDepartments = parseMultiValue(formData.ilgili_departman);
    const filteredUsers = selectedDepartments.length > 0
      ? users.filter((user) =>
          selectedDepartments.includes((user.department_name || '').trim())
        )
      : users;

    const fromDb = filteredUsers
      .map((user) => {
        const name = userLabel(user);
        if (!name) return null;
        const departmentSuffix = user.department_name
          ? ` (${translateDepartment(user.department_name)})`
          : '';
        return {
          value: name,
          label: `${name}${departmentSuffix}`,
        };
      })
      .filter(Boolean);

    const fromNotes = (filterOptions.ilgili_kisi || [])
      .map((name) => String(name ?? '').trim())
      .filter(Boolean)
      .map((name) => ({
        value: name,
        label: name,
      }));

    const merged = new Map();
    [...fromDb, ...fromNotes].forEach((option) => {
      if (option?.value) merged.set(option.value, option);
    });

    return [...merged.values()].sort(compareLabels);
  }, [users, formData.ilgili_departman, filterOptions.ilgili_kisi]);

  const filterFieldOptions = useMemo(
    () => ({
      ilgili_not_adi: toSelectOptions(filterOptions.ilgili_not_adi || []),
      not_tarihi: toDateFilterOptions(filterOptions.not_tarihi || []),
      ilgili_departman: departmentOptions,
      ilgili_kisi: mergeStringOptions(
        users.map((user) => userLabel(user)),
        filterOptions.ilgili_kisi || []
      ),
      durum: STATUS_OPTIONS,
    }),
    [filterOptions, departmentOptions, users]
  );

  const modalStatusOptions = useMemo(
    () => buildStatusOptions(formData.durum),
    [formData.durum]
  );

  const handleSearch = () => {
    setFilters(draftFilters);
    loadData(draftFilters, 1);
  };

  const handleClear = () => {
    const reset = {
      ilgili_not_adi: '',
      ilgili_departman: '',
      ilgili_kisi: '',
      durum: '',
      not_tarihi: '',
      search: '',
    };
    setDraftFilters(reset);
    setFilters(reset);
    loadData(reset, 1);
  };

  const resetModalState = () => {
    setFormData(EMPTY_FORM);
    setNewNoteImages([]);
    setNewReturnImages([]);
    setExistingNoteImages([]);
    setExistingReturnImages([]);
    setRemoveImageIds([]);
    setNewNoteDocuments([]);
    setNewReturnDocuments([]);
    setExistingNoteDocuments([]);
    setExistingReturnDocuments([]);
    setRemoveDocumentIds([]);
    setModalTab('not');
    setReturnEntries([]);
    setNewReturnText('');
    setReturnDraftOpen(false);
  };

  const openCreateModal = () => {
    setEditingRow(null);
    setSelectedRow(null);
    resetModalState();
    setIsModalOpen(true);
  };

  const refreshReturns = async (noteId) => {
    try {
      const resp = await docConvertAPI.getNoteReturns(noteId);
      setReturnEntries(resp?.data || []);
    } catch {
      // yoksay
    }
  };

  const submitReturn = async () => {
    const text = newReturnText.trim();
    if (!text || !editingRow?.id || returnBusy) return;
    setReturnBusy(true);
    try {
      await docConvertAPI.addNoteReturn(editingRow.id, text);
      setNewReturnText('');
      setReturnDraftOpen(false);
      setFormData((prev) => (prev.durum?.trim() ? prev : { ...prev, durum: 'Dönüş yapıldı' }));
      await refreshReturns(editingRow.id);
    } catch (err) {
      setError(err.message || 'Dönüş eklenemedi.');
    } finally {
      setReturnBusy(false);
    }
  };

  const removeReturn = async (returnId) => {
    if (!editingRow?.id) return;
    if (!window.confirm('Bu dönüşü silmek istiyor musunuz?')) return;
    try {
      await docConvertAPI.deleteNoteReturn(editingRow.id, returnId);
      await refreshReturns(editingRow.id);
    } catch (err) {
      setError(err.message || 'Dönüş silinemedi.');
    }
  };

  const openEditModal = async (row) => {
    if (!row) return;
    setEditingRow(row);
    setModalTab('not');
    setReturnEntries([]);
    setNewReturnText('');
    setReturnDraftOpen(false);
    setFormData({
      ilgili_not_adi: row.ilgili_not_adi || '',
      not_tarihi: toDateInputValue(row.not_tarihi),
      not_basligi: row.not_basligi || '',
      not: row.not || '',
      donusler: row.donusler || '',
      ilgili_departman: row.ilgili_departman || '',
      ilgili_kisi: row.ilgili_kisi || '',
      durum: row.durum || '',
    });
    setNewNoteImages([]);
    setNewReturnImages([]);
    setRemoveImageIds([]);
    setExistingNoteImages([]);
    setExistingReturnImages([]);
    setNewNoteDocuments([]);
    setNewReturnDocuments([]);
    setRemoveDocumentIds([]);
    setExistingNoteDocuments([]);
    setExistingReturnDocuments([]);
    setIsModalOpen(true);
    try {
      const response = await docConvertAPI.getDailyNote(row.id);
      const detail = response?.data ?? response;
      setExistingNoteImages(getNoteImages(detail));
      setExistingReturnImages(getReturnImages(detail));
      setExistingNoteDocuments(getNoteDocuments(detail));
      setExistingReturnDocuments(getReturnDocuments(detail));
      setReturnEntries(detail?.return_entries || []);
    } catch {
      setExistingNoteImages([]);
      setExistingReturnImages([]);
      setExistingNoteDocuments([]);
      setExistingReturnDocuments([]);
      setReturnEntries([]);
    }
  };

  useEffect(() => {
    const noteIdParam = searchParams.get('note_id');
    if (!noteIdParam || loading) return;

    const noteId = Number(noteIdParam);
    if (!Number.isFinite(noteId) || openedNoteIdRef.current === noteId) return;

    let cancelled = false;

    (async () => {
      const row = rows.find((item) => item.id === noteId);
      let target = row;
      if (!target) {
        try {
          const response = await docConvertAPI.getDailyNote(noteId);
          target = response?.data ?? response;
        } catch {
          return;
        }
      }
      if (cancelled || !target) return;

      openedNoteIdRef.current = noteId;
      await openEditModal(target);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('note_id');
        return next;
      }, { replace: true });
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, loading, rows, setSearchParams]);

  const handleDelete = async () => {
    if (!selectedRow) return;
    if (!window.confirm('Seçili notu silmek istediğinize emin misiniz?')) return;
    try {
      await docConvertAPI.deleteDailyNote(selectedRow.id);
      setSelectedRow(null);
      await loadData();
    } catch (err) {
      setError(err.message || 'Kayıt silinemedi.');
    }
  };

  const handleDeleteAction = async () => {
    if (selectedIds.size > 0) {
      await handleBulkDelete();
      return;
    }
    await handleDelete();
  };

  const deleteDisabled = selectedIds.size === 0 && !selectedRow;
  const deleteTitle =
    selectedIds.size > 0
      ? `${selectedIds.size} notu sil`
      : selectedRow
        ? 'Seçili satırı sil'
        : 'Sil';

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (
      !window.confirm(
        `${selectedIds.size} notu silmek istediğinize emin misiniz?`
      )
    ) {
      return;
    }
    try {
      await docConvertAPI.bulkDeleteDailyNotes([...selectedIds]);
      setSelectedIds(new Set());
      setSelectedRow(null);
      await loadData();
    } catch (err) {
      setError(err.message || 'Seçili notlar silinemedi.');
    }
  };

  const selectedNotes = useMemo(
    () => rows.filter((row) => selectedIds.has(row.id)),
    [rows, selectedIds]
  );

  const openMergeModal = () => {
    if (selectedIds.size < 2) return;
    // Varsayılan ana kayıt: en küçük sıra no (en üstteki).
    const sorted = [...selectedNotes].sort(
      (a, b) => (a.sira_no ?? Infinity) - (b.sira_no ?? Infinity)
    );
    setMergeTargetId(sorted[0]?.id ?? null);
    setIsMergeOpen(true);
  };

  const handleMerge = async () => {
    if (selectedIds.size < 2 || !mergeTargetId || mergeBusy) return;
    setMergeBusy(true);
    setError(null);
    try {
      await docConvertAPI.mergeDailyNotes([...selectedIds], mergeTargetId);
      setIsMergeOpen(false);
      setSelectedIds(new Set());
      setSelectedRow(null);
      await loadData();
    } catch (err) {
      setError(err.message || 'Notlar birleştirilemedi.');
    } finally {
      setMergeBusy(false);
    }
  };

  const openSendModal = () => {
    if (selectedIds.size < 1) return;
    setSendFolderId(null);
    setSendFolderName('Klasörsüz (kök)');
    setIsSendOpen(true);
  };

  const handleSend = async () => {
    if (selectedIds.size < 1 || sendBusy) return;
    setSendBusy(true);
    setError(null);
    try {
      await docConvertAPI.moveNotesToFolder([...selectedIds], sendFolderId);
      setIsSendOpen(false);
      setSelectedIds(new Set());
      setSelectedRow(null);
      await loadData();
    } catch (err) {
      setError(err.message || 'Notlar klasöre gönderilemedi.');
    } finally {
      setSendBusy(false);
    }
  };

  const toggleSelectRow = (rowId, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(rowId);
      else next.delete(rowId);
      return next;
    });
  };

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(new Set(rows.map((row) => row.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const allRowsSelected = rows.length > 0 && rows.every((row) => selectedIds.has(row.id));
  const someRowsSelected = rows.some((row) => selectedIds.has(row.id));

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = { ...formData };
      if (editingRow?.id) {
        await docConvertAPI.updateDailyNote(editingRow.id, payload, {
          noteImageFiles: newNoteImages,
          returnImageFiles: newReturnImages,
          noteDocumentFiles: newNoteDocuments,
          returnDocumentFiles: newReturnDocuments,
          removeImageIds,
          removeDocumentIds,
        });
      } else {
        await docConvertAPI.createDailyNote(payload, {
          noteImageFiles: newNoteImages,
          returnImageFiles: newReturnImages,
          noteDocumentFiles: newNoteDocuments,
          returnDocumentFiles: newReturnDocuments,
        });
      }
      setIsModalOpen(false);
      setEditingRow(null);
      setSelectedRow(null);
      resetModalState();
      await loadData();
    } catch (err) {
      setError(err.message || 'Kayıt kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const toggleRemoveImage = (imageId) => {
    setRemoveImageIds((prev) =>
      prev.includes(imageId) ? prev.filter((id) => id !== imageId) : [...prev, imageId]
    );
  };

  const toggleRemoveDocument = (documentId) => {
    setRemoveDocumentIds((prev) =>
      prev.includes(documentId) ? prev.filter((id) => id !== documentId) : [...prev, documentId]
    );
  };

  const handleDocumentDownloadError = useCallback((message) => {
    setError(message);
  }, []);

  const openImagePreview = useCallback((images, index = 0) => {
    if (!images?.length) return;
    setPreviewImages(toPreviewImages(images));
    setPreviewIndex(index);
    setIsPreviewOpen(true);
  }, []);

  const columns = useMemo(
    () => [
      {
        id: 'select',
        header: () => (
          <input
            type="checkbox"
            checked={allRowsSelected}
            ref={(input) => {
              if (input) input.indeterminate = someRowsSelected && !allRowsSelected;
            }}
            onChange={(event) => toggleSelectAll(event.target.checked)}
            onClick={(event) => event.stopPropagation()}
            title="Tümünü seç"
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
        ),
        enableSorting: false,
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedIds.has(row.original.id)}
            onChange={(event) => {
              event.stopPropagation();
              toggleSelectRow(row.original.id, event.target.checked);
            }}
            onClick={(event) => event.stopPropagation()}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
        ),
      },
      {
        header: 'Sıra No',
        accessorKey: 'sira_no',
        cell: ({ getValue }) => (
          <span className="font-semibold text-gray-700 dark:text-gray-200 tabular-nums">
            {getValue() ?? '-'}
          </span>
        ),
      },
      {
        header: 'İlgili Not Adı',
        accessorKey: 'ilgili_not_adi',
        cell: ({ getValue }) => (
          <span className="text-gray-600 dark:text-gray-300">{getValue() || '-'}</span>
        ),
      },
      {
        header: 'Not Tarihi',
        accessorKey: 'not_tarihi',
        cell: ({ getValue }) => (
          <span className="text-gray-600 dark:text-gray-300 whitespace-nowrap">
            {formatNoteDateDisplay(getValue())}
          </span>
        ),
      },
      {
        header: 'Not Başlığı',
        accessorKey: 'not_basligi',
        cell: ({ getValue }) => (
          <span className="font-medium text-gray-900 dark:text-white">{getValue() || '-'}</span>
        ),
      },
      {
        header: 'Not',
        accessorKey: 'not',
        enableSorting: false,
        cell: ({ getValue }) => (
          <span className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap">{getValue() || '-'}</span>
        ),
      },
      {
        header: 'Not Görselleri',
        id: 'note_images',
        enableSorting: false,
        cell: ({ row }) => (
          <ImageThumbnailStrip
            images={getNoteImages(row.original)}
            onOpenPreview={openImagePreview}
          />
        ),
      },
      {
        header: 'Not Dökümanları',
        id: 'note_documents',
        enableSorting: false,
        cell: ({ row }) => (
          <DocumentDownloadStrip
            documents={getNoteDocuments(row.original)}
            onDownloadError={handleDocumentDownloadError}
          />
        ),
      },
      {
        header: 'Dönüşler',
        accessorKey: 'donusler',
        enableSorting: false,
        cell: ({ row }) => {
          const entries = row.original.return_entries || [];
          const legacy = (row.original.donusler || '').trim();
          if (!entries.length && !legacy) {
            return <span className="text-gray-400">-</span>;
          }
          return (
            <div className="space-y-1.5 text-gray-600 dark:text-gray-300">
              {legacy && <div className="whitespace-pre-wrap">{legacy}</div>}
              {entries.map((entry, index) => (
                <div
                  key={entry.id ?? index}
                  className="whitespace-pre-wrap border-l-2 border-blue-200 pl-2 dark:border-blue-800"
                >
                  {entry.text}
                  <span className="mt-0.5 block text-[10px] text-gray-400">
                    {entry.author_name || '—'} · {formatDateTimeDisplay(entry.created_at)}
                  </span>
                </div>
              ))}
            </div>
          );
        },
      },
      {
        header: 'Dönüş Görselleri',
        id: 'return_images',
        enableSorting: false,
        cell: ({ row }) => (
          <ImageThumbnailStrip
            images={getReturnImages(row.original)}
            onOpenPreview={openImagePreview}
          />
        ),
      },
      {
        header: 'Dönüş Dökümanları',
        id: 'return_documents',
        enableSorting: false,
        cell: ({ row }) => (
          <DocumentDownloadStrip
            documents={getReturnDocuments(row.original)}
            onDownloadError={handleDocumentDownloadError}
          />
        ),
      },
      {
        header: 'İlgili Departmanlar',
        accessorKey: 'ilgili_departman',
        cell: ({ getValue }) => (
          <span className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
            {translateDepartments(getValue()) || '-'}
          </span>
        ),
      },
      {
        header: 'İlgili Kişiler',
        accessorKey: 'ilgili_kisi',
        cell: ({ getValue }) => (
          <span className="text-gray-600 dark:text-gray-300">{getValue() || '-'}</span>
        ),
      },
      {
        header: 'Durum',
        accessorKey: 'durum',
        cell: ({ getValue }) => {
          const value = getValue();
          if (!value) return <span className="text-gray-400">-</span>;
          return (
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusBadgeClass(value)}`}>
              {value}
            </span>
          );
        },
      },
    ],
    [openImagePreview, handleDocumentDownloadError, selectedIds, allRowsSelected, someRowsSelected, rows]
  );

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Günlük Notlar</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Excel ile aynı alan yapısında notları görüntüleyin ve düzenleyin.
        </p>
      </div>

      {error && (
        <div className="px-0">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded dark:bg-red-900/20 dark:border-red-800 dark:text-red-300 flex justify-between items-start">
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700">
              ×
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300">
          Loading data...
        </div>
      )}

      <ActionToolbar>
        <IconActionButton icon={PlusIcon} onClick={openCreateModal} title="Yeni Kayıt" />
        <IconActionButton
          icon={PencilSquareIcon}
          onClick={() => selectedRow && openEditModal(selectedRow)}
          disabled={!selectedRow}
          title="Düzenle"
        />
        <IconActionButton
          icon={ArrowsPointingInIcon}
          onClick={openMergeModal}
          disabled={selectedIds.size < 2}
          title={
            selectedIds.size < 2
              ? 'Birleştirmek için en az 2 not seçin'
              : `${selectedIds.size} notu birleştir`
          }
        />
        <IconActionButton
          icon={FolderArrowDownIcon}
          onClick={openSendModal}
          disabled={selectedIds.size < 1}
          title={
            selectedIds.size < 1
              ? 'Klasöre göndermek için not seçin'
              : `${selectedIds.size} notu klasöre gönder`
          }
        />
        <IconActionButton
          icon={TrashIcon}
          onClick={handleDeleteAction}
          disabled={deleteDisabled}
          title={deleteTitle}
          className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 disabled:text-gray-300"
        />
      </ActionToolbar>

      <FilterPanel title="Filters" isOpen={isFilterOpen} onToggle={() => setIsFilterOpen((v) => !v)}>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
          <div className="border border-gray-200 dark:border-gray-700 rounded-md p-2 xl:col-span-2">
            <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-2">Arama & Filtreler</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              <fieldset className="border border-gray-300 dark:border-gray-600 rounded-md px-2 pb-1 pt-0.5 bg-white dark:bg-gray-700 xl:col-span-2">
                <legend className="px-1 text-[10px] text-gray-500 dark:text-gray-400 leading-none">Metin Ara</legend>
                <input
                  type="text"
                  value={draftFilters.search}
                  onChange={(e) => setDraftFilters((prev) => ({ ...prev, search: e.target.value }))}
                  className="w-full text-[11px] bg-transparent outline-none text-gray-900 dark:text-white"
                  placeholder="Başlık, not, dönüş..."
                />
              </fieldset>
              {[
                ['ilgili_not_adi', 'İlgili Not Adı'],
                ['not_tarihi', 'Not Tarihi'],
                ['ilgili_departman', 'İlgili Departmanlar'],
                ['ilgili_kisi', 'İlgili Kişiler'],
                ['durum', 'Durum'],
              ].map(([key, label]) => (
                <fieldset
                  key={key}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-2 pb-1 pt-0.5 bg-white dark:bg-gray-700"
                >
                  <legend className="px-1 text-[10px] text-gray-500 dark:text-gray-400 leading-none">{label}</legend>
                  <Select
                    isClearable
                    isSearchable
                    options={filterFieldOptions[key] || []}
                    value={
                      draftFilters[key]
                        ? {
                          value: draftFilters[key],
                          label: key === 'not_tarihi'
                            ? formatNoteDateDisplay(draftFilters[key])
                            : draftFilters[key],
                        }
                        : null
                    }
                    onChange={(option) =>
                      setDraftFilters((prev) => ({ ...prev, [key]: option?.value || '' }))
                    }
                    styles={themedFilterSelectStyles}
                    classNamePrefix="daily-notes-filter-select"
                    classNames={filterSelectClassNames}
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                    placeholder=""
                  />
                </fieldset>
              ))}
            </div>
          </div>
        </div>
        <div className="border-t border-gray-100 dark:border-gray-700 pt-3 mt-3 flex justify-end">
          <FilterActions onClear={handleClear} onSearch={handleSearch} />
        </div>
      </FilterPanel>

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
        <DataTable
          data={rows}
          columns={columns}
          isLoading={loading}
          showSearch={false}
          compact
          compactCellWrap
          fixedHorizontalScroll
          manualSorting
          sorting={sorting}
          onSortingChange={handleSortingChange}
          onRowClick={setSelectedRow}
          onRowDoubleClick={openEditModal}
          selectedRowId={selectedRow?.id}
        />
      </div>

      {pageInfo.count > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 px-1 text-sm text-gray-600 dark:text-gray-300">
          <span>
            Toplam <span className="font-semibold">{pageInfo.count.toLocaleString('tr-TR')}</span> not
            {pageInfo.totalPages > 1 && <> · Sayfa {page}/{pageInfo.totalPages}</>}
          </span>
          {pageInfo.totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => goToPage(1)}
                disabled={page <= 1 || loading}
                className="rounded-md border border-gray-200 dark:border-gray-600 px-2.5 py-1 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                «
              </button>
              <button
                type="button"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1 || loading}
                className="rounded-md border border-gray-200 dark:border-gray-600 px-3 py-1 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Önceki
              </button>
              <button
                type="button"
                onClick={() => goToPage(page + 1)}
                disabled={page >= pageInfo.totalPages || loading}
                className="rounded-md border border-gray-200 dark:border-gray-600 px-3 py-1 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Sonraki
              </button>
              <button
                type="button"
                onClick={() => goToPage(pageInfo.totalPages)}
                disabled={page >= pageInfo.totalPages || loading}
                className="rounded-md border border-gray-200 dark:border-gray-600 px-2.5 py-1 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                »
              </button>
            </div>
          )}
        </div>
      )}

      <ResponsiveModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} className="max-w-5xl w-[min(100%,calc(100vw-2rem))]">
        <div className="px-4 pt-5 pb-4 sm:p-6 max-h-[88vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              {editingRow ? 'Notu Düzenle' : 'Yeni Not'}
            </h2>
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSave}>
            <div className="mb-4 flex gap-4 border-b border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setModalTab('not')}
                className={`-mb-px border-b-2 px-1 pb-2 text-sm font-medium transition-colors ${
                  modalTab === 'not'
                    ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                }`}
              >
                Not
              </button>
              <button
                type="button"
                onClick={() => setModalTab('donusler')}
                className={`-mb-px border-b-2 px-1 pb-2 text-sm font-medium transition-colors ${
                  modalTab === 'donusler'
                    ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                }`}
              >
                Dönüşler{returnEntries.length > 0 ? ` (${returnEntries.length})` : ''}
              </button>
            </div>

            <div className={modalTab === 'not' ? '' : 'hidden'}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">İlgili Not Adı</Label>
                <Input
                  value={formData.ilgili_not_adi}
                  onChange={(e) => setFormData((prev) => ({ ...prev, ilgili_not_adi: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Not Tarihi</Label>
                <Input
                  type="date"
                  value={formData.not_tarihi}
                  onChange={(e) => setFormData((prev) => ({ ...prev, not_tarihi: e.target.value }))}
                />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Not Başlığı</Label>
                <Input
                  value={formData.not_basligi}
                  onChange={(e) => setFormData((prev) => ({ ...prev, not_basligi: e.target.value }))}
                />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Not</Label>
                <textarea
                  rows={8}
                  value={formData.not}
                  onChange={(e) => setFormData((prev) => ({ ...prev, not: e.target.value }))}
                  className={NOTE_TEXTAREA_CLASS}
                />
              </div>
              <ModalImageField
                label="Not Görselleri"
                existingImages={existingNoteImages}
                removeImageIds={removeImageIds}
                onToggleRemove={toggleRemoveImage}
                onOpenPreview={openImagePreview}
                pendingFiles={newNoteImages}
                onFilesSelected={(files) => setNewNoteImages((prev) => [...prev, ...files])}
                onRemovePending={(index) => setNewNoteImages((prev) => prev.filter((_, i) => i !== index))}
                hint={
                  existingNoteImages.length > 0
                    ? 'Görsele tıklayarak büyütün; sağ üst X ile silmeyi işaretleyin.'
                    : undefined
                }
              />
              <ModalDocumentField
                label="Not Dökümanları"
                existingDocuments={existingNoteDocuments}
                pendingFiles={newNoteDocuments}
                removeDocumentIds={removeDocumentIds}
                onToggleRemove={toggleRemoveDocument}
                onFilesSelected={(files) => setNewNoteDocuments((prev) => [...prev, ...files])}
                onRemovePending={(index) => setNewNoteDocuments((prev) => prev.filter((_, i) => i !== index))}
                hint="Word, Excel, PDF ve benzeri dosyalar yükleyebilirsiniz (en fazla 20 MB)."
              />
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">İlgili Departmanlar</Label>
                <Select
                  isMulti
                  isClearable
                  isSearchable
                  closeMenuOnSelect={false}
                  options={departmentOptions}
                  value={findMultiSelectOptions(departmentOptions, formData.ilgili_departman)}
                  onChange={(options) =>
                    setFormData((prev) => ({
                      ...prev,
                      ilgili_departman: joinMultiValue((options || []).map((option) => option.value)),
                    }))
                  }
                  placeholder="Departman seçin veya arayın..."
                  styles={themedModalSelectStyles}
                  classNamePrefix="daily-notes-modal-select"
                  classNames={modalSelectClassNames}
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                  noOptionsMessage={() => 'Departman bulunamadı'}
                />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">İlgili Kişiler</Label>
                <Select
                  isMulti
                  isClearable
                  isSearchable
                  closeMenuOnSelect={false}
                  options={userOptions}
                  value={findMultiSelectOptions(userOptions, formData.ilgili_kisi)}
                  onChange={(options) =>
                    setFormData((prev) => ({
                      ...prev,
                      ilgili_kisi: joinMultiValue((options || []).map((option) => option.value)),
                    }))
                  }
                  placeholder="Kişi seçin veya arayın..."
                  styles={themedModalSelectStyles}
                  classNamePrefix="daily-notes-modal-select"
                  classNames={modalSelectClassNames}
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                  noOptionsMessage={() => 'Kullanıcı bulunamadı'}
                />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Durum</Label>
                <StatusPicker
                  value={formData.durum}
                  options={modalStatusOptions}
                  onChange={(nextValue) =>
                    setFormData((prev) => ({ ...prev, durum: nextValue }))
                  }
                />
              </div>
            </div>
            </div>

            <div className={modalTab === 'donusler' ? 'space-y-6' : 'hidden'}>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                    Dönüş Kayıtları (kişi + tarih)
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setReturnDraftOpen(true)}
                    disabled={!editingRow?.id || returnDraftOpen}
                  >
                    + Dönüş Ekle
                  </Button>
                </div>
                {!editingRow?.id && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    Dönüş eklemek için önce notu kaydedin.
                  </p>
                )}
                {returnDraftOpen && (
                  <div className="space-y-2 rounded-lg border border-blue-200 bg-blue-50/50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
                    <textarea
                      rows={3}
                      value={newReturnText}
                      onChange={(e) => setNewReturnText(e.target.value)}
                      placeholder="Dönüş metnini yazın…"
                      className={NOTE_TEXTAREA_CLASS}
                      autoFocus
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setReturnDraftOpen(false);
                          setNewReturnText('');
                        }}
                      >
                        Vazgeç
                      </Button>
                      <Button
                        type="button"
                        onClick={submitReturn}
                        disabled={returnBusy || !newReturnText.trim()}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        {returnBusy ? 'Ekleniyor…' : 'Ekle'}
                      </Button>
                    </div>
                  </div>
                )}
                {returnEntries.length > 0 ? (
                  <div className="space-y-2">
                    {returnEntries.map((entry) => (
                      <div
                        key={entry.id}
                        className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
                      >
                        <div className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-100">
                          {entry.text}
                        </div>
                        <div className="mt-2 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                          <span>
                            {entry.author_name || '—'} · {formatDateTimeDisplay(entry.created_at)}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeReturn(entry.id)}
                            className="inline-flex items-center gap-1 text-red-500 hover:text-red-600"
                          >
                            <XMarkIcon className="h-3.5 w-3.5" /> Sil
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  !returnDraftOpen && (
                    <p className="text-xs text-gray-400">Henüz dönüş kaydı eklenmemiş.</p>
                  )
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
                <div className="space-y-1 md:col-span-2">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    Dönüş Metni (serbest / içe aktarılan)
                  </Label>
                  <textarea
                    rows={6}
                    value={formData.donusler}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        donusler: value,
                        durum: prev.durum?.trim() ? prev.durum : value.trim() ? 'Dönüş yapıldı' : prev.durum,
                      }));
                    }}
                    className={NOTE_TEXTAREA_CLASS}
                  />
                </div>
                <ModalImageField
                  label="Dönüş Görselleri"
                  existingImages={existingReturnImages}
                  removeImageIds={removeImageIds}
                  onToggleRemove={toggleRemoveImage}
                  onOpenPreview={openImagePreview}
                  pendingFiles={newReturnImages}
                  onFilesSelected={(files) => {
                    setNewReturnImages((prev) => [...prev, ...files]);
                    if (files.length) {
                      setFormData((prev) => (prev.durum?.trim() ? prev : { ...prev, durum: 'Dönüş yapıldı' }));
                    }
                  }}
                  onRemovePending={(index) => setNewReturnImages((prev) => prev.filter((_, i) => i !== index))}
                  hint={
                    existingReturnImages.length > 0
                      ? 'Dönüş sırasında eklenen görseller burada listelenir.'
                      : undefined
                  }
                />
                <ModalDocumentField
                  label="Dönüş Dökümanları"
                  existingDocuments={existingReturnDocuments}
                  pendingFiles={newReturnDocuments}
                  removeDocumentIds={removeDocumentIds}
                  onToggleRemove={toggleRemoveDocument}
                  onFilesSelected={(files) => {
                    setNewReturnDocuments((prev) => [...prev, ...files]);
                    if (files.length) {
                      setFormData((prev) => (prev.durum?.trim() ? prev : { ...prev, durum: 'Dönüş yapıldı' }));
                    }
                  }}
                  onRemovePending={(index) => setNewReturnDocuments((prev) => prev.filter((_, i) => i !== index))}
                  hint="Dönüşe ait Word, Excel, PDF vb. dosyaları buradan ekleyin."
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end space-x-3">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                İptal
              </Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
                {saving ? 'Kaydediliyor…' : 'Kaydet'}
              </Button>
            </div>
          </form>
        </div>
      </ResponsiveModal>

      <ResponsiveModal
        isOpen={isMergeOpen}
        onClose={() => !mergeBusy && setIsMergeOpen(false)}
        className="max-w-2xl w-[min(100%,calc(100vw-2rem))]"
      >
        <div className="px-4 pt-5 pb-4 sm:p-6 max-h-[88vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              Notları Birleştir ({selectedNotes.length})
            </h2>
            <button
              type="button"
              onClick={() => !mergeBusy && setIsMergeOpen(false)}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
            Ana kayıt olarak seçtiğiniz notun başlığı, tarihi ve adı korunur. Diğer notların
            metni, dönüşleri, görselleri ve dökümanları bu nota taşınır; birleştirilen diğer
            notlar silinir.
          </p>

          <div className="space-y-2">
            {[...selectedNotes]
              .sort((a, b) => (a.sira_no ?? Infinity) - (b.sira_no ?? Infinity))
              .map((note) => {
                const isTarget = note.id === mergeTargetId;
                return (
                  <label
                    key={note.id}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                      isTarget
                        ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20'
                        : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/40'
                    }`}
                  >
                    <input
                      type="radio"
                      name="merge-target"
                      checked={isTarget}
                      onChange={() => setMergeTargetId(note.id)}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          #{note.sira_no ?? '-'}
                        </span>
                        <span className="truncate text-sm font-medium text-gray-900 dark:text-white">
                          {note.not_basligi || '(başlıksız)'}
                        </span>
                        {isTarget && (
                          <span className="ml-auto shrink-0 rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-medium text-white">
                            Ana kayıt
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        {formatNoteDateDisplay(note.not_tarihi)}
                        {note.ilgili_not_adi ? ` · ${note.ilgili_not_adi}` : ''}
                      </div>
                      {note.not && (
                        <p className="mt-1 line-clamp-2 whitespace-pre-wrap text-xs text-gray-600 dark:text-gray-300">
                          {note.not}
                        </p>
                      )}
                    </div>
                  </label>
                );
              })}
          </div>

          <div className="mt-6 flex items-center justify-end space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsMergeOpen(false)}
              disabled={mergeBusy}
            >
              İptal
            </Button>
            <Button
              type="button"
              onClick={handleMerge}
              disabled={mergeBusy || !mergeTargetId}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {mergeBusy ? 'Birleştiriliyor…' : 'Birleştir'}
            </Button>
          </div>
        </div>
      </ResponsiveModal>

      <ResponsiveModal
        isOpen={isSendOpen}
        onClose={() => !sendBusy && setIsSendOpen(false)}
        className="max-w-lg w-[min(100%,calc(100vw-2rem))]"
      >
        <div className="px-4 pt-5 pb-4 sm:p-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              Klasöre Gönder ({selectedIds.size})
            </h2>
            <button
              type="button"
              onClick={() => !sendBusy && setIsSendOpen(false)}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
            Hedef klasörü seçin. Klasörler "Klasörler" ekranından oluşturulur.
          </p>

          <FolderPicker
            value={sendFolderId}
            onChange={(id, name) => {
              setSendFolderId(id);
              setSendFolderName(name);
            }}
          />

          <div className="mt-3 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600 dark:bg-gray-700/40 dark:text-gray-300">
            Hedef: <span className="font-medium text-gray-900 dark:text-white">{sendFolderName}</span>
          </div>

          <div className="mt-6 flex items-center justify-end space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsSendOpen(false)}
              disabled={sendBusy}
            >
              İptal
            </Button>
            <Button
              type="button"
              onClick={handleSend}
              disabled={sendBusy}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {sendBusy ? 'Gönderiliyor…' : 'Gönder'}
            </Button>
          </div>
        </div>
      </ResponsiveModal>

      <ImagePreview
        images={previewImages}
        currentIndex={previewIndex}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        onNavigate={setPreviewIndex}
      />
    </div>
  );
};

export default DailyNotesPage;
